import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

/**
 * Spreadsheet-like inline editable cell.
 * - type "number": right aligned, commits parsed float (empty -> 0, negatives clamped to 0 unless allowNegative)
 * - type "text": left aligned
 * Commits on blur or Enter.
 */
export const EditableCell = ({
  value,
  onSave,
  type = "number",
  allowNegative = false,
  placeholder = "",
  testId,
  className,
  suffix,
}) => {
  const [val, setVal] = useState(value ?? "");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!focused) {
      setVal(value ?? "");
    }
  }, [value, focused]);

  const commit = () => {
    setFocused(false);
    if (type === "number") {
      let n = parseFloat(String(val).replace(/,/g, ""));
      if (isNaN(n)) n = 0;
      if (!allowNegative && n < 0) n = 0;
      setVal(n);
      if (n !== Number(value ?? 0)) {
        onSave(n);
      }
    } else {
      const t = String(val);
      if (t !== String(value ?? "")) {
        onSave(t);
      }
    }
  };

  const inputEl = (
    <input
      ref={inputRef}
      data-testid={testId}
      value={val}
      placeholder={placeholder}
      inputMode={type === "number" ? "decimal" : "text"}
      onChange={(e) => setVal(e.target.value)}
      onFocus={(e) => {
        setFocused(true);
        e.target.select();
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          setVal(value ?? "");
          setFocused(false);
          e.currentTarget.blur();
        }
      }}
      className={cn(
        "h-9 w-full rounded-md border border-input bg-white px-2 font-mono tabular-nums text-sm outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        type === "number" ? "text-right" : "text-left",
        suffix && "pr-7",
        className
      )}
    />
  );

  if (suffix) {
    return (
      <div className="relative">
        {inputEl}
        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center font-mono text-xs text-muted-foreground">
          {suffix}
        </span>
      </div>
    );
  }

  return inputEl;
};
